import React, { useState, useCallback, useEffect } from 'react';
import { 
  Box, 
  Pagination, 
  Table, 
  TableBody, 
  TableContainer, 
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Paper 
} from '@mui/material';

/**
 * Reusable sortable and paginated table component
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - The array of data to display in the table
 * @param {Array} props.columns - Column configuration array with objects containing:
 *                              - id: unique identifier for the column
 *                              - label: display label for the column
 *                              - render: (optional) custom render function for cell
 *                              - align: (optional) text alignment
 *                              - sortable: (optional) boolean to enable sorting
 *                              - getValue: (optional) function to get the sort value from a row
 * @param {number} [props.rowsPerPage=10] - Number of rows per page
 * @param {string} [props.emptyMessage="No data available"] - Message to show when data is empty
 * @param {Object} [props.tableContainerProps={}] - Additional props for TableContainer
 * @param {Object} [props.tableProps={}] - Additional props for Table
 * @param {Object} [props.paginationProps={}] - Additional props for Pagination
 * @param {Function} [props.onSortChange] - Callback when sort changes (orderBy, order) => void
 * @param {string} [props.initialOrderBy] - Initial column to sort by
 * @param {string} [props.initialOrder='asc'] - Initial sort direction ('asc' or 'desc')
 * @param {boolean} [props.isContractDetailTable=false] - Whether this is a contract detail table
 * @param {string} [props.stickyColumn] - The column ID to make sticky (usually first column)
 * @returns {React.ReactNode}
 */
const SortablePaginatedTable = ({ 
  data = [],
  columns = [],
  rowsPerPage = 10,
  emptyMessage = "No data available",
  tableContainerProps = {},
  tableProps = {},
  paginationProps = {},
  onSortChange,
  initialOrderBy = null,
  initialOrder = 'asc',
  isContractDetailTable = false,
  stickyColumn = null
}) => {
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [order, setOrder] = useState(initialOrder);
  
  // Update internal state if external sort props change
  useEffect(() => {
    if (initialOrderBy !== null) {
      setOrderBy(initialOrderBy);
    }
    if (initialOrder !== null) {
      setOrder(initialOrder);
    }
  }, [initialOrderBy, initialOrder]);
  
  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };
  
  // Handle sort request
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    
    // Notify parent component of sort change
    if (onSortChange) {
      onSortChange(property, newOrder);
    }
  };
  
  // Reset sorting when columns change
  useEffect(() => {
    // Check if the current orderBy column exists in the new column set
    if (orderBy && !columns.some(col => col.id === orderBy)) {
      setOrderBy(null);
      setOrder('asc');
      
      // Notify parent component of sort change
      if (onSortChange) {
        onSortChange(null, 'asc');
      }
    }
  }, [columns, orderBy, onSortChange]);
  
  // Sort function
  const sortData = useCallback((data) => {
    if (!orderBy) return data;
    
    try {
      // Find the column configuration for this property
      const column = columns.find(col => col.id === orderBy);
      
      // If the column isn't found in the current column set, return unsorted data
      if (!column) return data;
      
      return [...data].sort((a, b) => {
        // Use the getValue function if provided, otherwise use the property directly
        let aValue, bValue;
        
        try {
          aValue = column.getValue ? column.getValue(a) : a[orderBy];
          bValue = column.getValue ? column.getValue(b) : b[orderBy];
        } catch (error) {
          console.error('Error accessing sort values:', error);
          return 0; // Return 0 to maintain original order if we can't get values
        }
        
        // Handle undefined/null values - always sort them to the end
        if (aValue === undefined || aValue === null) {
          return order === 'asc' ? 1 : -1;
        }
        if (bValue === undefined || bValue === null) {
          return order === 'asc' ? -1 : 1;
        }
        
        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return order === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // Handle number comparison
        if (order === 'asc') {
          return (aValue ?? 0) - (bValue ?? 0);
        } else {
          return (bValue ?? 0) - (aValue ?? 0);
        }
      });
    } catch (error) {
      console.error('Error sorting data:', error);
      return data; // Return unsorted data if sorting fails
    }
  }, [orderBy, order, columns]);
  
  // Get paginated and sorted data
  const getSortedPaginatedData = useCallback(() => {
    try {
      // First sort
      const sortedData = sortData(data);
      
      // Then paginate
      const startIndex = (page - 1) * rowsPerPage;
      return sortedData.slice(startIndex, startIndex + rowsPerPage);
    } catch (error) {
      console.error('Error in getSortedPaginatedData:', error);
      return data.slice(0, rowsPerPage); // Return first page of unsorted data if there's an error
    }
  }, [data, page, rowsPerPage, sortData]);
  
  // If data changes and current page is out of bounds, reset to page 1
  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(data.length / rowsPerPage));
    if (page > maxPage) {
      setPage(1);
    }
  }, [data, rowsPerPage, page]);
  
  // If no data, show empty message
  if (!data || data.length === 0) {
    return <Box sx={{ py: 2 }}>{emptyMessage}</Box>;
  }
  
  // Header styling - purple headers for sortable tables except contract details
  const headerStyles = !isContractDetailTable ? {
    backgroundColor: 'rgb(138, 66, 236) !important',
    color: 'white !important',
    '& .MuiTableSortLabel-root': {
      color: 'white !important',
      '&.Mui-active': {
        color: 'white !important',
      },
    },
    '& .MuiTableSortLabel-icon': {
      color: 'white !important',
    }
  } : {};
  
  return (
    <>
      <TableContainer 
        component={Paper} 
        variant="outlined" 
        sx={{ mb: 2, ...tableContainerProps.sx }}
        {...tableContainerProps}
      >
        <Table {...tableProps}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell 
                  key={column.id}
                  align={column.align || 'left'}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{
                    ...headerStyles,
                    ...(stickyColumn === column.id ? {
                      position: 'sticky',
                      left: 0,
                      backgroundColor: !isContractDetailTable ? 'rgb(138, 66, 236) !important' : '#f8f9fa',
                      zIndex: 3
                    } : {})
                  }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {getSortedPaginatedData().map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column) => (
                  <TableCell 
                    key={`${rowIndex}-${column.id}`}
                    align={column.align || 'left'}
                    sx={{
                      ...(stickyColumn === column.id ? {
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        zIndex: 2,
                        boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)'
                      } : {})
                    }}
                    {...(column.cellProps ? column.cellProps(row[column.id], row) : {})}
                  >
                    {column.render ? column.render(row) : row[column.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Only show pagination if we have more than one page */}
      {data.length > rowsPerPage && (
        <Box display="flex" justifyContent="center" my={2}>
          <Pagination 
            count={Math.ceil(data.length / rowsPerPage)} 
            page={page} 
            onChange={handlePageChange}
            color="primary"
            {...paginationProps}
          />
        </Box>
      )}
    </>
  );
};

export default SortablePaginatedTable; 